import { Component, OnInit } from '@angular/core';
import {
  MlAdminOperationsSnapshot,
  MlLifecycleActionItemDto,
  MlLifecycleOverviewDto,
  MlLifecycleStageDto
} from '../../../_core/_model/machine-learning';
import { MachineLearningOperationsOverviewService } from '../../../_core/_services/machine-learning-operations-overview.service';

interface MlDashboardAlert {
  code: string;
  severity: string;
  message: string;
  count: number;
  route: string;
  source: string;
}

interface MlDashboardQuickLink {
  title: string;
  description: string;
  route: string;
  badge?: number | null;
  status?: string | null;
}

@Component({
  selector: 'app-admin-machine-learning-dashboard',
  standalone: false,
  templateUrl: './admin-machine-learning-dashboard.html',
  styleUrl: './admin-machine-learning-dashboard.css'
})
export class AdminMachineLearningDashboard implements OnInit {
  loading = false;
  error = '';
  snapshot: MlAdminOperationsSnapshot | null = null;

  constructor(private overviewService: MachineLearningOperationsOverviewService) {}

  ngOnInit(): void {
    this.load(false);
  }

  get overview(): MlLifecycleOverviewDto | null {
    return this.snapshot?.lifecycle ?? null;
  }

  load(forceRefresh = true): void {
    this.loading = true;
    this.error = '';

    this.overviewService.getSnapshot(forceRefresh).subscribe({
      next: snapshot => {
        this.snapshot = snapshot;
        this.loading = false;

        if (!snapshot.lifecycle) {
          this.error = 'The lifecycle overview is unavailable. Operational modules that loaded successfully are still shown below.';
        }
      },
      error: error => {
        this.loading = false;
        this.error = error?.message || 'Unable to load the machine-learning operations overview.';
      }
    });
  }

  get labelledCoveragePercent(): number {
    const total = this.overview?.totalPredictions ?? 0;
    const labelled = this.overview?.labelledPredictions ?? 0;

    if (total <= 0) {
      return 0;
    }

    return Math.min(100, Math.round((labelled / total) * 100));
  }

  get openWithdrawalReviews(): number {
    const summary = this.snapshot?.withdrawalRisk;
    return (summary?.pending ?? 0)
      + (summary?.submitting ?? 0)
      + (summary?.reconciliationRequired ?? 0);
  }

  get openDepositReviews(): number {
    const summary = this.snapshot?.depositRisk;
    return (summary?.pending ?? 0)
      + (summary?.inReview ?? 0)
      + (summary?.sourceOfFundsRequested ?? 0)
      + (summary?.escalated ?? 0);
  }

  get activeAlertCount(): number {
    return this.combinedAlerts.length;
  }

  get criticalLiquidityCorridors(): number {
    return this.snapshot?.liquidity?.criticalCorridors ?? 0;
  }

  get partialErrors(): string[] {
    return this.snapshot?.partialErrors ?? [];
  }

  get overallStatusClass(): string {
    return this.getStatusClass(this.overview?.overallStatus);
  }

  get deploymentStatusClass(): string {
    return this.getStatusClass(this.snapshot?.deploymentHealth?.healthStatus);
  }

  get combinedAlerts(): MlDashboardAlert[] {
    const snapshot = this.snapshot;
    if (!snapshot) return [];

    const alerts: MlDashboardAlert[] = [
      ...snapshot.withdrawalAlerts.map(item => ({
        code: item.code,
        severity: item.severity,
        message: item.message,
        count: item.count,
        route: '/admin/machine-learning/withdrawal-risk',
        source: 'Withdrawal risk'
      })),
      ...snapshot.depositAlerts.map(item => ({
        code: item.code,
        severity: item.severity,
        message: item.message,
        count: item.count,
        route: '/admin/machine-learning/deposit-risk',
        source: 'Deposit risk'
      })),
      ...snapshot.liquidityAlerts.map(item => ({
        code: item.code,
        severity: item.severity,
        message: item.message,
        count: 1,
        route: '/admin/machine-learning/liquidity',
        source: `${item.fromCurrency}/${item.toCurrency}`
      })),
      ...snapshot.deploymentAlerts.map(item => ({
        code: item.code,
        severity: item.severity,
        message: item.message,
        count: 1,
        route: '/admin/machine-learning/deployment-health',
        source: 'Deployment health'
      })),
      ...snapshot.operationalAlerts.map(item => ({
        code: item.code,
        severity: item.severity,
        message: item.message,
        count: item.count,
        route: '/admin/machine-learning/operational-reliability',
        source: item.providerCode || 'Operational reliability'
      }))
    ];

    return alerts.sort((a, b) => this.severityRank(a.severity) - this.severityRank(b.severity));
  }

  get quickLinks(): MlDashboardQuickLink[] {
    return [
      {
        title: 'Withdrawal review queue',
        description: 'Held payouts, stale reviews and reconciliation work.',
        route: '/admin/machine-learning/withdrawal-risk',
        badge: this.openWithdrawalReviews
      },
      {
        title: 'Deposit compliance queue',
        description: 'Risk reviews, source-of-funds recommendations and escalations.',
        route: '/admin/machine-learning/deposit-risk',
        badge: this.openDepositReviews
      },
      {
        title: 'Candidate governance',
        description: 'Review candidates waiting for shadow or promotion decisions.',
        route: '/admin/machine-learning/governance',
        badge: (this.snapshot?.candidateGovernance?.pendingReview ?? 0)
          + (this.snapshot?.candidateGovernance?.promotionReady ?? 0)
      },
      {
        title: 'Liquidity intelligence',
        description: 'Review corridor deficits, surpluses and forecast alerts.',
        route: '/admin/machine-learning/liquidity',
        badge: this.criticalLiquidityCorridors
      },
      {
        title: 'Deployment health',
        description: 'Inspect canary performance, fallback rate and rollback guidance.',
        route: '/admin/machine-learning/deployment-health',
        status: this.snapshot?.deploymentHealth?.healthStatus || 'No deployment'
      },
      {
        title: 'Operational reliability',
        description: 'Settlement backlog, gateway failures and webhook latency.',
        route: '/admin/machine-learning/operational-reliability',
        badge: this.snapshot?.operationalAlerts.length ?? 0
      }
    ];
  }

  getActionRoute(action: MlLifecycleActionItemDto): string | null {
    const code = (action.code || '').toUpperCase();

    if (code.includes('TRAINING') || code.includes('LABEL')) {
      return '/admin/machine-learning/training';
    }

    if (code.includes('CANDIDATE') || code.includes('SHADOW') || code.includes('GOVERNANCE')) {
      return '/admin/machine-learning/governance';
    }

    if (code.includes('DEPLOYMENT') || code.includes('ROLLBACK') || code.includes('PROMOTION')) {
      return '/admin/machine-learning/deployment-controls';
    }

    return null;
  }

  getStatusClass(status?: string | null): string {
    switch ((status || '').toLowerCase()) {
      case 'healthy':
      case 'ready':
      case 'completed':
      case 'active':
        return 'text-bg-success';
      case 'inprogress':
      case 'in_progress':
      case 'selectedforshadow':
      case 'watch':
      case 'canary':
        return 'text-bg-primary';
      case 'attentionrequired':
      case 'insufficientdata':
      case 'pendingreview':
        return 'text-bg-warning text-dark';
      case 'critical':
      case 'blocked':
      case 'unavailable':
      case 'failed':
        return 'text-bg-danger';
      case 'disabled':
      case 'notconfigured':
      case 'nodeployment':
        return 'text-bg-secondary';
      default:
        return 'text-bg-light text-dark border';
    }
  }

  getSeverityClass(severity?: string | null): string {
    switch ((severity || '').toLowerCase()) {
      case 'critical':
        return 'border-danger severity-critical';
      case 'high':
        return 'border-warning severity-high';
      case 'medium':
        return 'border-primary severity-medium';
      default:
        return 'border-secondary severity-info';
    }
  }

  getSeverityBadgeClass(severity?: string | null): string {
    switch ((severity || '').toLowerCase()) {
      case 'critical':
        return 'text-bg-danger';
      case 'high':
        return 'text-bg-warning text-dark';
      case 'medium':
        return 'text-bg-primary';
      default:
        return 'text-bg-secondary';
    }
  }

  getProgressBarClass(stage: MlLifecycleStageDto): string {
    const status = (stage.status || '').toLowerCase();

    if (['critical', 'blocked', 'failed', 'unavailable'].includes(status)) {
      return 'bg-danger';
    }

    if (['attentionrequired', 'insufficientdata', 'pendingreview'].includes(status)) {
      return 'bg-warning';
    }

    if (['healthy', 'ready', 'completed', 'active'].includes(status)) {
      return 'bg-success';
    }

    return 'bg-primary';
  }

  normalizedProgress(value?: number | null): number {
    if (value == null || Number.isNaN(value)) {
      return 0;
    }

    return Math.min(100, Math.max(0, value));
  }

  trackStage(_: number, stage: MlLifecycleStageDto): string {
    return stage.code;
  }

  trackAction(_: number, action: MlLifecycleActionItemDto): string {
    return action.code;
  }

  trackAlert(_: number, alert: MlDashboardAlert): string {
    return `${alert.source}:${alert.code}`;
  }

  trackQuickLink(_: number, link: MlDashboardQuickLink): string {
    return link.route;
  }

  private severityRank(severity?: string | null): number {
    switch ((severity || '').toLowerCase()) {
      case 'critical': return 0;
      case 'high': return 1;
      case 'medium': return 2;
      default: return 3;
    }
  }
}
